import React, { useEffect, useRef, useState } from 'react';
import '../css/FeatureShowcase.css';

// ── Core feature videos ──────────────────────────────────────────────────────
// Each tab owns a short, muted walkthrough. The clips match DURATION_MS so the
// media and the feature-list progress indicator advance together.
const FEATURES = [
  {
    id: 'widget',
    title: 'Customisable buyback form',
    description:
      'Build branded questions, answer types and required fields, then publish the form to your storefront.',
    video: '/videos/core-feature-buyback-form.mp4',
  },
  {
    id: 'workflow',
    title: 'End-to-end, from offer to payment',
    description:
      'Track acceptance, receiving, inspection and payout in one workflow while the customer stays updated.',
    video: '/videos/core-feature-offer-to-payment.mp4',
  },
  {
    id: 'shopify',
    title: 'Fully integrated with Shopify',
    description:
      'Issue discount codes, import or export products, and create draft orders without duplicate admin.',
    video: '/videos/core-feature-shopify.mp4',
  },
];

const DURATION_MS = 7000; // how long each feature stays active before advancing

const FeatureShowcase = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0); // bump to restart the progress bar
  const timer = useRef(null);
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Only run the auto-advance while the section is on screen.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.35,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (paused || !inView) return undefined;
    timer.current = setTimeout(() => {
      setActive((i) => (i + 1) % FEATURES.length);
      setCycle((c) => c + 1);
    }, DURATION_MS);
    return () => clearTimeout(timer.current);
  }, [active, paused, inView, cycle]);

  const select = (i) => {
    setActive(i);
    setCycle((c) => c + 1);
  };

  const current = FEATURES[active];

  return (
    <section
      id="features"
      ref={sectionRef}
      className="feature-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="feature-showcase__inner">
        <div className="feature-showcase__head">
          <h2>See the core features in action</h2>
          <p>Short walkthroughs of what Furloop does day to day. Pick a feature or let it play.</p>
        </div>

        <div className="feature-showcase__grid">
          {/* ── Left: feature list ─────────────────────────────────── */}
          <ol className="feature-showcase__list" role="tablist" aria-label="Core features">
            {FEATURES.map((f, i) => {
              const isActive = i === active;
              return (
                <li key={f.id} className={isActive ? 'is-active' : ''}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`feature-panel-${f.id}`}
                    onClick={() => select(i)}
                  >
                    <span className="feature-showcase__num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="feature-showcase__title">{f.title}</span>
                    <span className="feature-showcase__desc">{f.description}</span>
                    <span className="feature-showcase__bar" aria-hidden="true">
                      <span
                        key={`${cycle}-${i}`}
                        className="feature-showcase__bar-fill"
                        style={{
                          animationDuration: `${DURATION_MS}ms`,
                          animationPlayState: paused || !inView ? 'paused' : 'running',
                        }}
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {/* ── Right: media panel ─────────────────────────────────── */}
          <div
            className="feature-showcase__media"
            role="tabpanel"
            id={`feature-panel-${current.id}`}
            aria-label={current.title}
          >
            {current.video ? (
              <video
                key={current.id}
                src={current.video}
                aria-label={`${current.title} product walkthrough`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="feature-showcase__placeholder" key={current.id}>
                <div className="feature-showcase__placeholder-chrome">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="feature-showcase__placeholder-body">
                  <div className="feature-showcase__play" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="28" height="28">
                      <path d="M8 5v14l11-7z" fill="currentColor" />
                    </svg>
                  </div>
                  <p className="feature-showcase__placeholder-title">{current.title}</p>
                  <p className="feature-showcase__placeholder-note">Video coming soon</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
